// A venue's rate card — pay-as-you-go priced per station class (F-PRC-10, F-PRC-11, F-PRC-12; spec/06 §4).
//
//   GET  /v1/organizations/{o}/venues/{v}/rate-card   the version in force, the ones scheduled, the history   pricing:read
//   POST /v1/organizations/{o}/venues/{v}/rate-card   publish a new version                                      pricing:manage
//
// A card is **versioned**: publishing writes a new version with an `effectiveFrom` (now, or a later moment), and the
// version in force at a moment is the newest one whose `effectiveFrom` has passed. Versions are never edited or
// deleted, so what a session was charged can always be traced to the card it was priced by; a session keeps the
// rate it started at (spec/05 §14), so publishing never reprices play already running.
//
// Each line prices one **station class**: an hourly amount, the billing unit (60, 300, 900 or 1800 seconds), a
// minimum charge (0–3600 seconds of play, once per session) and an optional daily cap, plus time-of-day **bands**
// that override the hourly amount (`{ days, start, end, hourlyAmount }` in the venue's timezone, half-open, a band
// that crosses midnight belonging to the day it starts on; no two bands of a class may overlap). The rules are
// `@clavian/domain` `rateCardProblems`, run by the server and by the console's editor alike.
//
// **No card, no change.** A venue that has never published a card — or a class its card leaves out — is priced at
// the venue's flat `sessions.paygHourlyRate` and `sessions.paygBillingUnitSeconds`, exactly as before cards existed;
// the view names that fallback so the console can show it.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { RateCardVersionIdSchema, StationClassIdSchema, VenueIdSchema } from "../ids/schema.js";
import { NonNegativeMoneySchema, PositiveMoneySchema } from "../primitives/money.js";
import { IsoDateTimeSchema, LocalTimeSchema, TimeWindowDaySchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { PRICING_MANAGE, PRICING_READ } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
/** The billing units a card allows (spec/06 §4). */
export const RATE_CARD_BILLING_UNITS = [60, 300, 900, 1800];
/** The longest minimum charge a card may set: an hour of play (spec/06 §4). */
export const RATE_CARD_MAX_MINIMUM_CHARGE_SECONDS = 3600;
/** A time-of-day override of a class's hourly amount ("off-peak 01:00–11:00 at ₹40/h"). */
export const RateCardBandSchema = z
    .object({
    days: z.array(TimeWindowDaySchema).min(1).max(8),
    start: LocalTimeSchema,
    end: LocalTimeSchema,
    hourlyAmount: NonNegativeMoneySchema,
})
    .openapi('RateCardBand', {
    description: 'A time-of-day price for a class, in the venue’s timezone. `end` before `start` crosses midnight; bands of a class may not overlap.',
});
/** The units an Open console session's time played is rounded up to (seconds): a minute to an hour (ADR-0034). */
export const RATE_CARD_CONTROLLER_BILLING_UNITS = [60, 300, 900, 1800, 3600];
/** The most fixed block prices one class's controller pricing carries. */
export const RATE_CARD_MAX_BLOCK_PRICES = 8;
/** A fixed price for the first controller of a whole block ("1 h = ₹150"). */
export const RateCardBlockPriceSchema = z
    .object({
    /** The block's length in minutes: 30, 60, 120, 300, or any other whole number of minutes up to a day. */
    minutes: z.number().int().min(1).max(1440),
    amount: NonNegativeMoneySchema,
})
    .openapi('RateCardBlockPrice', { description: 'The first controller’s price for a whole booked block of exactly `minutes` (ADR-0034).' });
/**
 * How a **staff-run** class (a console, a sim, a table, ADR-0034) is priced: sessions there are booked in blocks for a
 * number of controllers and paid once, at the end. The first controller pays the block's fixed price when one is set
 * for exactly that length, else `firstControllerHourly` for the time booked; every extra controller pays
 * `extraControllerHourly`; an Open session pays for the time played, rounded up to `billingUnitSeconds`.
 */
export const RateCardControllerPricingSchema = z
    .object({
    firstControllerHourly: NonNegativeMoneySchema,
    extraControllerHourly: NonNegativeMoneySchema,
    blockPrices: z.array(RateCardBlockPriceSchema).max(RATE_CARD_MAX_BLOCK_PRICES),
    /** 60, 300, 900, 1800 or 3600: what an Open session's time played is rounded up to. */
    billingUnitSeconds: z.number().int().min(60).max(3600),
})
    .openapi('RateCardControllerPricing', { description: 'Controller pricing for a staff-run class: rates, block prices, the Open billing unit (ADR-0034).' });
/** One station class's line on the card. */
export const RateCardRateSchema = z
    .object({
    stationClassId: StationClassIdSchema,
    /** The price of an hour of play outside any band. */
    hourlyAmount: NonNegativeMoneySchema,
    /** The unit play is billed in; a started unit is charged in full. 60, 300, 900 or 1800. */
    billingUnitSeconds: z.number().int().min(60).max(1800),
    /** The least a session pays for play on this class, in seconds of play at the rate. 0 turns it off. */
    minimumChargeSeconds: z.number().int().min(0).max(RATE_CARD_MAX_MINIMUM_CHARGE_SECONDS),
    /**
     * Once a player's pay-as-you-go charges at the venue reach this in a business day, further play that day is free.
     * Not charged by the sessions spine yet (spec/06 §4 implementation status): a publish must send `null`.
     */
    dailyCapAmount: PositiveMoneySchema.nullable(),
    bands: z.array(RateCardBandSchema).max(24),
    /**
     * A staff-run class's controller pricing (ADR-0034). Absent: a staff-run session on the class is priced with
     * `hourlyAmount` for every controller, the line's billing unit for Open, and no block prices. Ignored for PCs.
     */
    controllers: RateCardControllerPricingSchema.optional(),
})
    .openapi('RateCardRate', { description: 'A station class’s pay-as-you-go price on a rate card (spec/06 §4).' });
export const RateCardVersionStatusSchema = z
    .enum(['in_force', 'scheduled', 'superseded'])
    .openapi('RateCardVersionStatus', { description: 'in_force: the version pricing play now; scheduled: effective later; superseded: replaced.' });
/** One published version of a venue's rate card. */
export const RateCardVersionSchema = z
    .object({
    id: RateCardVersionIdSchema,
    venueId: VenueIdSchema,
    /** 1 for the venue's first card, then one more with each publish. */
    version: z.number().int().min(1),
    name: z.string(),
    /** When it starts pricing play. */
    effectiveFrom: IsoDateTimeSchema,
    publishedAt: IsoDateTimeSchema,
    status: RateCardVersionStatusSchema,
    /** One line per class it prices; a class left out is priced at the venue's flat rate. */
    rates: z.array(RateCardRateSchema),
})
    .openapi('RateCardVersion', { description: 'One published version of a venue’s rate card (spec/06 §4, F-PRC-10).' });
/** The venue's flat pay-as-you-go rate: what a class the card does not price is charged (spec/06 §4). */
export const RateCardFallbackSchema = z
    .object({
    hourlyAmount: NonNegativeMoneySchema,
    billingUnitSeconds: z.number().int().min(60).max(1800),
})
    .openapi('RateCardFallback', {
    description: 'The venue’s flat `sessions.paygHourlyRate` and billing unit, charged to every class the card in force does not price.',
});
/**
 * What a class pays now under the version in force — the band open in the venue's timezone — and when that next
 * changes ("₹60/h now · ₹40/h from 01:00", spec/06 §4). Resolved by the server, which prices every session.
 */
/** Named before its nullable use (NAMED_BEFORE_THEIR_NULLABLE_USE, #450). */
export const RateCardBandNowSchema = z.object({ start: LocalTimeSchema, end: LocalTimeSchema }).openapi('RateCardBandNow');
/** Named before its nullable use (NAMED_BEFORE_THEIR_NULLABLE_USE, #450). */
export const RateCardNextChangeSchema = z.object({ at: IsoDateTimeSchema, hourlyAmount: NonNegativeMoneySchema }).openapi('RateCardNextChange');
export const RateCardClassNowSchema = z
    .object({
    stationClassId: StationClassIdSchema,
    hourlyAmount: NonNegativeMoneySchema,
    /** The band in force, or null for the class's base rate. */
    band: RateCardBandNowSchema.nullable(),
    nextChange: RateCardNextChangeSchema.nullable(),
})
    .openapi('RateCardClassNow');
export const RateCardViewSchema = z
    .object({
    venueId: VenueIdSchema,
    /** The venue's currency: every amount on its card is in it. */
    currency: z.string().length(3),
    /** The venue's IANA timezone, which the bands are read in. */
    timezone: z.string(),
    /** Whether the venue sells pay-as-you-go play at all (`sessions.payAsYouGoEnabled`). */
    payAsYouGoEnabled: z.boolean(),
    fallback: RateCardFallbackSchema,
    /** Versions published with a later `effectiveFrom`, soonest first. */
    scheduled: z.array(RateCardVersionSchema),
    /** Every version, newest first (the latest 20). */
    history: z.array(RateCardVersionSchema),
    /**
     * The version pricing play now, or null when none has been published (the flat rate applies). After the lists on
     * purpose: the first reference to `RateCardVersion` names the component, and a nullable one would make it nullable.
     */
    current: RateCardVersionSchema.nullable(),
    /** Each class the version in force prices: its rate now and its next change. Empty with no card in force. */
    now: z.array(RateCardClassNowSchema),
    serverTime: IsoDateTimeSchema,
})
    .openapi('RateCardView', { description: 'A venue’s rate card: in force, scheduled, history, and the flat fallback (spec/06 §4).' });
export const PublishRateCardSchema = z
    .object({
    name: z.string().trim().min(1).max(60),
    /** When it starts pricing play; absent is now. Never in the past. */
    effectiveFrom: IsoDateTimeSchema.optional(),
    rates: z.array(RateCardRateSchema).max(50),
})
    .strict()
    .openapi('PublishRateCard', {
    description: 'A new version of the venue’s rate card. Validated by the rules of spec/06 §4 (`rateCardProblems`). Audited.',
});
const viewResponse = (description) => ({
    description,
    content: { [JSON_CONTENT_TYPE]: { schema: RateCardViewSchema } },
});
export const getVenueRateCardRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/rate-card',
    operationId: 'getVenueRateCard',
    tags: ['pricing'],
    summary: 'Read a venue’s rate card',
    description: 'The rate card version pricing pay-as-you-go play now, the versions scheduled, the history, and the venue’s flat rate that prices any class the card leaves out (spec/06 §4). Requires `pricing:read`.',
    [PERMISSION_EXTENSION]: PRICING_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: viewResponse('The venue’s rate card.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const publishVenueRateCardRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/rate-card',
    operationId: 'publishVenueRateCard',
    tags: ['pricing'],
    summary: 'Publish a new version of a venue’s rate card',
    description: 'Publishes a version of the venue’s rate card — per station class, with bands, billing unit, minimum charge and daily cap — effective now or from a later moment (spec/06 §4). ' +
        'Running sessions keep the rate they started at. Audited. Requires `pricing:manage`.',
    [PERMISSION_EXTENSION]: PRICING_MANAGE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: PublishRateCardSchema } }, required: true },
    },
    responses: {
        201: viewResponse('The venue’s rate card, with the version published.'),
        400: problemResponse('The card breaks a rule of spec/06 §4: an overlapping band, a billing unit, a class of another venue.', 'validation-failed'),
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        409: problemResponse('The effective moment is in the past.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=rate-cards.js.map